import {
  faCloudUploadAlt,
  faFileCode,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { appInsights } from "AppInsights";
import { submitRemitNotification } from "api/account/client";
import { BlueOutlineButton } from "components/components/BlueOutlineButton/BlueOutlineButton";
import Button from "components/components/Button/Button";
import Icon from "components/components/Icon/Icon";
import { IconSize } from "components/components/Icon/IconWrapper";
import {
  RemitSubmissionState,
  RemitUploadActionType,
  useRemitUpload,
} from "contexts/RemitUploadContext";
import React, { useCallback, useRef, useState } from "react";
import colours from "styles/colours";

import { getRemitSubmissionErrorMessage } from "./remitUploadFormHelpers";
import {
  DropZone,
  DropZoneText,
  ErrorText,
  RemoveFileButton,
  SelectedFileContainer,
  SelectedFileName,
  SubmitButtonContainer,
  UploadIcon,
  UploadSection,
} from "./style";

const SCHEMA_LINK =
  "https://github.com/elexon-data/REMIT-Submissions-API-Developer-Guide/blob/main/schemas/remit2_1.xsd";
const MAX_FILE_SIZE_IN_KB = 128;
const MAX_FILE_SIZE_IN_BYTES = MAX_FILE_SIZE_IN_KB * 1024;
const MAX_FILE_SIZE_DESCRIPTION = `${MAX_FILE_SIZE_IN_KB} KB`;

const RemitUploadForm: React.FC = () => {
  const { state: remitState, dispatch } = useRemitUpload();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSubmitting =
    remitState.submissionState === RemitSubmissionState.Submitting;

  const validateFile = useCallback((file: File): boolean => {
    if (!file.name.toLowerCase().endsWith(".xml")) {
      setError("Invalid file type. Please upload an XML file.");
      return false;
    }
    if (file.size > MAX_FILE_SIZE_IN_BYTES) {
      setError(
        `File size exceeds ${MAX_FILE_SIZE_DESCRIPTION}. Please upload a smaller file.`
      );
      return false;
    }
    setError(null);
    return true;
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    },
    [validateFile]
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(true);
    },
    []
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
    },
    []
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleChooseFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedFile) {
      setError("Please select an XML file to submit.");
      return;
    }

    setError(null);
    dispatch({ type: RemitUploadActionType.StartSubmission });

    try {
      await submitRemitNotification(selectedFile);
      dispatch({ type: RemitUploadActionType.SubmissionComplete });
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      appInsights.trackException({
        exception: err instanceof Error ? err : new Error(String(err)),
        severityLevel: 3,
        properties: { context: "REMIT submission" },
      });
      const errorMessage = getRemitSubmissionErrorMessage(err);
      dispatch({
        type: RemitUploadActionType.SubmissionError,
        message: errorMessage,
      });
    }
  }, [selectedFile, dispatch]);

  return (
    <UploadSection data-test-id="remit-upload-section">
      <h2>Upload a REMIT notification</h2>
      <p>
        Upload a REMIT notification in XML format here. Once submitted, the file
        will be validated and processed. See the{" "}
        <a
          href={SCHEMA_LINK}
          target="_blank"
          rel="noreferrer"
          data-test-id="remit-schema-link"
          className="is-link"
        >
          Elexon schema for REMIT notification
        </a>
        . Please note that submissions are rate limited.
      </p>

      <DropZone
        isDragOver={isDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={!selectedFile ? handleChooseFileClick : undefined}
        data-test-id="remit-drop-zone"
        aria-label="Drop zone for XML file upload"
      >
        {selectedFile ? (
          <SelectedFileContainer data-test-id="remit-selected-file">
            <Icon
              iconName={faFileCode}
              colour={colours.elexonBlue}
              size={IconSize.medium}
            />
            <SelectedFileName>{selectedFile.name}</SelectedFileName>
            <RemoveFileButton
              onClick={(e): void => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              data-test-id="remit-remove-file-button"
              aria-label="Remove selected file"
            >
              <Icon iconName={faTimes} colour={colours.elexonRed} />
            </RemoveFileButton>
          </SelectedFileContainer>
        ) : (
          <>
            <UploadIcon>
              <Icon iconName={faCloudUploadAlt} colour={colours.mediumGrey} />
            </UploadIcon>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml"
          onChange={handleInputChange}
          style={{ display: "none" }}
          data-test-id="remit-file-input"
          aria-label="Upload XML file"
        />

        <DropZoneText>Drag and drop your XML file here, or</DropZoneText>
        <BlueOutlineButton
          buttonText="Browse file"
          onClick={(e): void => {
            e.stopPropagation();
            handleChooseFileClick();
          }}
          dataTestId="remit-choose-file-button"
        />

        {error && (
          <ErrorText data-test-id="remit-upload-error" role="alert">
            {error}
          </ErrorText>
        )}
      </DropZone>

      <SubmitButtonContainer>
        <Button
          buttonText="Submit notification"
          onClick={handleSubmit}
          disabled={!selectedFile || isSubmitting}
          dataTestId="remit-submit-button"
        />
      </SubmitButtonContainer>
    </UploadSection>
  );
};

export default RemitUploadForm;
